// ── Real estate types ──

export type PricingModel = "m2" | "unit";

export type RealEstateKey =
  | "kantoren"
  | "winkels"
  | "magazijn"
  | "logistiek"
  | "appartement"
  | "rusthuiskamer"
  | "studentenkamer"
  | "cohousingKamer"
  | "parking";

export const DEFAULT_RE_PRICING_MODEL: Record<RealEstateKey, PricingModel> = {
  kantoren: "m2",
  winkels: "m2",
  magazijn: "m2",
  logistiek: "m2",
  appartement: "unit",
  rusthuiskamer: "unit",
  studentenkamer: "unit",
  cohousingKamer: "unit",
  parking: "unit",
};

/** @deprecated Kept for backward compat with saved simulations */
export interface RealEstatePricing {
  kantoren: { perM2: number };
  winkels: { perM2: number };
  magazijn: { perM2: number };
  logistiek: { perM2: number };
  appartement: { perUnit: number };
  rusthuiskamer: { perUnit: number };
  studentenkamer: { perUnit: number };
  cohousingKamer: { perUnit: number };
}

/** Alias for the configurable pricing model map */
export type RealEstatePricingModelMap = Record<RealEstateKey, PricingModel>;

/** % multiplier per RE category (1.0 = 100% of base unit/m² price) */
export type CategoryMultipliers = Record<RealEstateKey, number>;

/** A volume discount breakpoint: from X units/m² → Y% discount */
export interface VolumeDiscountTier {
  from: number;      // threshold (units or m²)
  discountPct: number; // discount percentage (e.g. 20 = 20%)
}

/** Volume discounts per RE category, sorted ascending by `from` */
export type VolumeDiscounts = Record<RealEstateKey, VolumeDiscountTier[]>;

/** Get the applicable discount % for a given quantity. NOT cumulative. */
export function getVolumeDiscount(
  quantity: number,
  discountTiers: VolumeDiscountTier[]
): number {
  let discount = 0;
  for (const tier of discountTiers) {
    if (quantity >= tier.from) discount = tier.discountPct;
    else break;
  }
  return discount;
}

export const DEFAULT_VOLUME_DISCOUNTS: VolumeDiscounts = {
  kantoren: [{ from: 5000, discountPct: 10 }, { from: 15000, discountPct: 20 }],
  winkels: [{ from: 5000, discountPct: 10 }, { from: 15000, discountPct: 20 }],
  magazijn: [{ from: 10000, discountPct: 10 }, { from: 30000, discountPct: 20 }],
  logistiek: [{ from: 10000, discountPct: 10 }, { from: 30000, discountPct: 20 }],
  appartement: [{ from: 50, discountPct: 10 }, { from: 200, discountPct: 20 }],
  rusthuiskamer: [{ from: 50, discountPct: 10 }, { from: 200, discountPct: 20 }],
  studentenkamer: [{ from: 100, discountPct: 10 }, { from: 300, discountPct: 20 }],
  cohousingKamer: [{ from: 50, discountPct: 10 }, { from: 200, discountPct: 20 }],
  parking: [{ from: 100, discountPct: 10 }, { from: 300, discountPct: 20 }],
};

export const DEFAULT_CATEGORY_MULTIPLIERS: CategoryMultipliers = {
  kantoren: 1.0,
  winkels: 1.0,
  magazijn: 0.5,
  logistiek: 0.5,
  appartement: 1.0,
  rusthuiskamer: 0.5,
  studentenkamer: 0.5,
  cohousingKamer: 0.5,
  parking: 0.25,
};

// ── Modules (dynamic) ──

export type ModuleCategory =
  | "algemeen"
  | "vastgoed"
  | "documenten"
  | "financieel"
  | "acquisitie"
  | "crm"
  | "ai"
  | "integraties";

/** A single functionality row within a module */
export interface ModuleFunctionality {
  id: string;
  label: string;
  /** Default value for customer offers: "✓", "", or custom text */
  defaultValue?: string;
  /** Per-tier value: "✓" for included, "" for not included, or custom text */
  tiers: Record<string, string>;
}

export interface ModuleItem {
  label: string;
  description: string;
  icon: string;
  category: ModuleCategory;
  enabled: boolean;
  functionalities?: ModuleFunctionality[];
  pricePerUnit: number;   // base price per unit
  pricePerM2: number;     // base price per m²
}

/** Dynamic map of module key → module config+pricing */
export type ModulePricing = Record<string, ModuleItem>;
export type ModuleKey = string;

// ── Tiers ──

export interface TierConfig {
  label: string;
  icon: string;
  description: string;
  color: string;
  isFree?: boolean;
  isPopular?: boolean;
  basePrice: number;
  pricePerUnit: number;
  pricePerM2: number;
  includedUsers: number;
  pricePerExtraUser: number;
  includedModules: string[];
  freeMaxUnits?: number;
  freeMaxM2?: number;
}

/** A row in the comparison table */
export interface ComparisonRow {
  id: string;
  label: string;
  tiers: Record<string, boolean>;
}

/** Dynamic map of tier key → tier config. Keys are user-defined. */
export type TierPricing = Record<string, TierConfig>;
export type TierKey = string;

// ── Customer offers ──

export interface ModuleOverride {
  moduleKey: string;
  discountPct: number;         // 0-100 percentage discount (100 = free, 50 = half price)
  months?: number;             // how many months (undefined/0 = permanent)
  note?: string;               // e.g. "Beta programma", "Eerste 3 maanden gratis"
  /** @deprecated Use discountPct instead. Kept for backward compat with saved offers. */
  priceOverride?: number;
  /** @deprecated Use months instead. Kept for backward compat with saved offers. */
  trialMonths?: number;
}

export interface CustomerOffer {
  id?: number;
  customerName: string;
  contactEmail?: string;
  contactPhone?: string;
  notes?: string;
  createdByUserId?: string;
  createdByUserName?: string;
  // Portfolio
  tier: TierKey;
  units: Record<RealEstateKey, number>;
  extraUsers: number;
  // Module selection
  selectedModules: string[];
  moduleOverrides: ModuleOverride[];
  // Pricing
  additionalDiscountPct: number;
  fixedPriceYear1?: number;    // negotiated fixed MRR for year 1
  fixedPriceYear2?: number;    // negotiated fixed MRR for year 2
  oneOffSetupFee: number;
  // Computed (stored for reference)
  calculatedMRR?: number;
  calculatedARR?: number;
  // Meta
  status: "draft" | "sent" | "accepted" | "declined";
  validUntil?: string;
  createdAt?: string;
  updatedAt?: string;
}

// ── Revenue simulation ──

export interface ManualCustomer {
  id: string;
  name: string;
  tier: TierKey;
  units: Record<RealEstateKey, number>;
  extraUsers: number;
  /** Custom module selection — if undefined, uses tier's default modules */
  selectedModules?: string[];
  /** Additional discount % on top of volume discounts (commercial negotiation) */
  additionalDiscountPct?: number;
  /** Fixed negotiated price per year (key = year number, e.g. 2026). Overrides calculated MRR for that year. */
  fixedPricePerYear?: Record<number, number>;
  /** One-off fee — only counts in the year it's entered */
  oneOffPayment: number;
}

export interface TierProjection {
  clientCount: number;
  /** Average units/m² per client, broken down by RE type */
  avgUnitsPerClient: Record<RealEstateKey, number>;
}

export interface YearSlice {
  year: number;
  manualCustomers: ManualCustomer[];
  projections: Record<TierKey, TierProjection>;
}

// ── Top-level simulation ──

export interface PricingSimulation {
  id?: number;
  name: string;
  description?: string;
  createdByUserId?: string;
  createdByUserName?: string;
  realEstatePricing?: RealEstatePricing; // legacy, kept for backward compat
  rePricingModel?: RealEstatePricingModelMap;
  categoryMultipliers?: CategoryMultipliers;
  volumeDiscounts?: VolumeDiscounts;
  modulePricing: ModulePricing;
  tierPricing: TierPricing;
  customPricingThreshold?: number;
  comparisonRows?: ComparisonRow[];
  years?: YearSlice[];
  clientVolumes?: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
}

// ── Defaults ──

export const DEFAULT_REAL_ESTATE_PRICING: RealEstatePricing = {
  kantoren: { perM2: 1.0 },
  winkels: { perM2: 1.0 },
  magazijn: { perM2: 0.25 },
  logistiek: { perM2: 0.25 },
  appartement: { perUnit: 180.0 },
  rusthuiskamer: { perUnit: 84.0 },
  studentenkamer: { perUnit: 84.0 },
  cohousingKamer: { perUnit: 84.0 },
};

export const DEFAULT_MODULE_PRICING: ModulePricing = {
  verhuur: {
    label: "Verhuur", description: "Huurtracking, indexatie, leegstand, kandidaten", icon: "\uD83D\uDD04", category: "vastgoed", enabled: true, pricePerUnit: 3.0, pricePerM2: 0.03,
    functionalities: [
      { id: "v1", label: "Huurtracking & opvolging", tiers: { free: "\u2714", starter: "\u2714", growth: "\u2714", pro: "\u2714" } },
      { id: "v2", label: "Huurindexatie berekening", tiers: { free: "Manueel", starter: "Manueel", growth: "Geautomatiseerd", pro: "Geautomatiseerd" } },
      { id: "v3", label: "Leegstandbeheer", tiers: { free: "", starter: "\u2714", growth: "\u2714", pro: "\u2714" } },
      { id: "v4", label: "Kandidaatbeheer", tiers: { free: "", starter: "\u2714", growth: "\u2714", pro: "\u2714" } },
      { id: "v5", label: "Automatische verlengingen", tiers: { free: "", starter: "", growth: "\u2714", pro: "\u2714" } },
    ],
  },
  helpdesk: {
    label: "Helpdesk", description: "Ticketsysteem, huurderscommunicatie", icon: "\uD83D\uDEA8", category: "vastgoed", enabled: true, pricePerUnit: 2.0, pricePerM2: 0.02,
    functionalities: [
      { id: "h1", label: "Ticketsysteem voor huurders", tiers: { free: "", starter: "\u2714", growth: "\u2714", pro: "\u2714" } },
      { id: "h2", label: "Communicatie via SMS en e-mail", tiers: { free: "", starter: "\u2714", growth: "\u2714", pro: "\u2714" } },
      { id: "h3", label: "Automatische communicatie", tiers: { free: "", starter: "", growth: "\u2714", pro: "\u2714" } },
      { id: "h4", label: "SLA-rapporten", tiers: { free: "", starter: "", growth: "\u2714", pro: "\u2714" } },
    ],
  },
  onderhoud:         { label: "Onderhoud",            description: "Onderhoudsplanning, aannemerbeheer",                   icon: "\uD83D\uDD27", category: "vastgoed",     enabled: true,  pricePerUnit: 2.0,  pricePerM2: 0.02 },
  voorzieningen:     { label: "Voorzieningen",        description: "Assets, brandveiligheid, klimatisatie, energie",       icon: "\uD83D\uDCE6", category: "vastgoed",     enabled: true,  pricePerUnit: 1.5,  pricePerM2: 0.015 },
  renovatie:         { label: "Renovatie",            description: "Projecten, investeringslijnen, budgetten",             icon: "\uD83E\uDDF1", category: "vastgoed",     enabled: true,  pricePerUnit: 1.5,  pricePerM2: 0.015 },
  verzekeringen:     { label: "Verzekeringen",        description: "Polisbeheer, schadeclaims",                            icon: "\u2602\uFE0F", category: "vastgoed",     enabled: true,  pricePerUnit: 1.0,  pricePerM2: 0.01 },
  documenten:        { label: "Documenten",           description: "Gecentraliseerde opslag, mappenstructuur",             icon: "\uD83D\uDCC1", category: "documenten",   enabled: true,  pricePerUnit: 1.5,  pricePerM2: 0.015 },
  fotos:             { label: "Foto's",               description: "Fotobeheer gebouwen en units",                         icon: "\uD83D\uDCF7", category: "documenten",   enabled: true,  pricePerUnit: 1.0,  pricePerM2: 0.01 },
  documentGenerator: { label: "Document Generator",   description: "Templates, automatische documentcreatie",              icon: "\uD83D\uDCC4", category: "documenten",   enabled: true,  pricePerUnit: 1.5,  pricePerM2: 0.015 },
  whatsapp:          { label: "WhatsApp",             description: "Huurders- en leverancierscommunicatie",                icon: "\uD83D\uDCAC", category: "documenten",   enabled: true,  pricePerUnit: 1.0,  pricePerM2: 0.01 },
  huurcontracten:    { label: "Huurcontracten",       description: "e-Sign, sjablonen, registratie, verlenging",           icon: "\uD83D\uDCCB", category: "financieel",   enabled: true,  pricePerUnit: 2.0,  pricePerM2: 0.02 },
  schuldvordering:   { label: "Schuldvordering",      description: "Aanmaningen, escalatieflows, incasso",                 icon: "\u2696\uFE0F", category: "financieel",   enabled: true,  pricePerUnit: 2.0,  pricePerM2: 0.02 },
  acquisitie:        { label: "Acquisitie",           description: "Deal pipeline, rendementsanalyse",                     icon: "\uD83C\uDFAF", category: "acquisitie",   enabled: true,  pricePerUnit: 2.0,  pricePerM2: 0.02 },
  dueDiligence:      { label: "Due Diligence",        description: "DD-checklists, investeringsrapporten",                 icon: "\uD83D\uDD0D", category: "acquisitie",   enabled: true,  pricePerUnit: 2.0,  pricePerM2: 0.02 },
  crm:               { label: "CRM",                  description: "Contacten, bedrijven, relatiebeheer",                  icon: "\uD83D\uDC65", category: "crm",          enabled: true,  pricePerUnit: 1.5,  pricePerM2: 0.015 },
  taken:             { label: "Taken",                description: "Taakbeheer, toewijzing, opvolging",                    icon: "\u2705",       category: "crm",          enabled: true,  pricePerUnit: 1.0,  pricePerM2: 0.01 },
  aiHuurprijsbenchmark: { label: "AI Huurprijsbenchmark", description: "Marktconforme huurprijsanalyse",                  icon: "\uD83E\uDD16", category: "ai",           enabled: true,  pricePerUnit: 1.5,  pricePerM2: 0.015 },
  aiDocumentAnalyse: { label: "AI Document Analyse",  description: "Automatische documentverwerking en extractie",         icon: "\uD83E\uDDE0", category: "ai",           enabled: true,  pricePerUnit: 1.5,  pricePerM2: 0.015 },
  aiDealAnalyse:     { label: "AI Deal Analyse",      description: "Investeringsanalyse en scoring",                       icon: "\uD83D\uDCCA", category: "ai",           enabled: true,  pricePerUnit: 2.0,  pricePerM2: 0.02 },
  aiAssistent:       { label: "AI Assistent (Ella)",  description: "Conversatie-AI voor vastgoedbeheer",                   icon: "\u2728",       category: "ai",           enabled: true,  pricePerUnit: 2.0,  pricePerM2: 0.02 },
  customIntegraties: { label: "Custom integraties",   description: "Odoo, SAP, SharePoint, Google Drive",                  icon: "\u2699\uFE0F", category: "integraties",  enabled: false, pricePerUnit: 0,    pricePerM2: 0 },
};

export const DEFAULT_TIER_PRICING: TierPricing = {
  free: {
    label: "Free", icon: "\uD83D\uDD13", description: "Startpunt voor eigenaars met \u00E9\u00E9n of twee panden", color: "#9CA3AF",
    isFree: true, basePrice: 0, pricePerUnit: 0, pricePerM2: 0, includedUsers: 1, pricePerExtraUser: 0,
    includedModules: ["verhuur"], freeMaxUnits: 3, freeMaxM2: 500,
  },
  starter: {
    label: "Starter", icon: "\uD83C\uDFE0", description: "Voor actieve eigenaars en kleine vastgoedbeheerders", color: "#1D9E75",
    basePrice: 49, pricePerUnit: 1.8, pricePerM2: 0.02, includedUsers: 2, pricePerExtraUser: 9,
    includedModules: ["verhuur", "helpdesk", "huurcontracten", "documenten", "fotos", "crm", "taken"],
  },
  growth: {
    label: "Growth", icon: "\uD83C\uDFE2", description: "Voor groeiende portfolios met automatisering", color: "#185FA5",
    isPopular: true, basePrice: 149, pricePerUnit: 1.4, pricePerM2: 0.015, includedUsers: 5, pricePerExtraUser: 15,
    includedModules: ["verhuur", "helpdesk", "onderhoud", "voorzieningen", "huurcontracten", "schuldvordering", "documenten", "fotos", "documentGenerator", "whatsapp", "crm", "taken", "verzekeringen", "aiHuurprijsbenchmark", "aiDocumentAnalyse"],
  },
  pro: {
    label: "Pro", icon: "\uD83C\uDFE6", description: "Voor professionele vastgoedbeheerders", color: "#7C3AED",
    basePrice: 349, pricePerUnit: 0.9, pricePerM2: 0.01, includedUsers: 10, pricePerExtraUser: 19,
    includedModules: ["verhuur", "helpdesk", "onderhoud", "voorzieningen", "renovatie", "verzekeringen", "documenten", "fotos", "documentGenerator", "whatsapp", "huurcontracten", "schuldvordering", "acquisitie", "dueDiligence", "crm", "taken", "aiHuurprijsbenchmark", "aiDocumentAnalyse", "aiDealAnalyse", "aiAssistent"],
  },
};

export const DEFAULT_COMPARISON_ROWS: ComparisonRow[] = [
  { id: "1", label: "Huurtracking & indexatie",       tiers: { free: true,  starter: true,  growth: true,  pro: true } },
  { id: "2", label: "Basisrapportering",               tiers: { free: true,  starter: true,  growth: true,  pro: true } },
  { id: "3", label: "Helpdesk ticketing",              tiers: { free: false, starter: true,  growth: true,  pro: true } },
  { id: "4", label: "Onderhoudsworkflows",             tiers: { free: false, starter: false, growth: true,  pro: true } },
  { id: "5", label: "AI huurprijsbenchmark",           tiers: { free: false, starter: false, growth: true,  pro: true } },
  { id: "6", label: "Document generator",              tiers: { free: false, starter: false, growth: true,  pro: true } },
  { id: "7", label: "Acquisitie & due diligence",     tiers: { free: false, starter: false, growth: false, pro: true } },
  { id: "8", label: "AI deal analyse",                 tiers: { free: false, starter: false, growth: false, pro: true } },
  { id: "9", label: "API-toegang",                     tiers: { free: false, starter: false, growth: false, pro: true } },
  { id: "10", label: "White-label optie",              tiers: { free: false, starter: false, growth: false, pro: true } },
];

const defaultAvg = (overrides: Partial<Record<RealEstateKey, number>>): Record<RealEstateKey, number> => ({
  ...EMPTY_UNITS,
  ...overrides,
});

export function createDefaultYear(year: number): YearSlice {
  return {
    year,
    manualCustomers: [],
    projections: {
      free:    { clientCount: 500, avgUnitsPerClient: defaultAvg({ appartement: 3 }) },
      starter: { clientCount: 60,  avgUnitsPerClient: defaultAvg({ appartement: 10, kantoren: 200 }) },
      growth:  { clientCount: 25,  avgUnitsPerClient: defaultAvg({ appartement: 30, kantoren: 500, magazijn: 1000 }) },
      pro:     { clientCount: 8,   avgUnitsPerClient: defaultAvg({ appartement: 80, kantoren: 2000, magazijn: 3000, winkels: 500 }) },
    },
  };
}

/** Sum all RE values for a projection's avg units */
export function projectionTotalUnits(avg: Record<RealEstateKey, number>): number {
  return Object.values(avg).reduce((s, v) => s + v, 0);
}



export const EMPTY_UNITS: Record<RealEstateKey, number> = {
  kantoren: 0, winkels: 0, magazijn: 0, logistiek: 0,
  appartement: 0, rusthuiskamer: 0, studentenkamer: 0, cohousingKamer: 0, parking: 0,
};

export const REAL_ESTATE_LABELS: Record<RealEstateKey, { label: string; unit: string; icon: string }> = {
  kantoren:       { label: "Kantoren",        unit: "m\u00B2", icon: "\uD83C\uDFE2" },
  winkels:        { label: "Winkels",         unit: "m\u00B2", icon: "\uD83C\uDFEA" },
  magazijn:       { label: "Magazijn",        unit: "m\u00B2", icon: "\uD83C\uDFED" },
  logistiek:      { label: "Logistiek",       unit: "m\u00B2", icon: "\uD83D\uDE9A" },
  appartement:    { label: "Appartement & Studio", unit: "unit", icon: "\uD83C\uDFE0" },
  rusthuiskamer:  { label: "Rusthuiskamer",   unit: "unit",     icon: "\uD83C\uDFE5" },
  studentenkamer: { label: "Studentenkamer",  unit: "unit",     icon: "\uD83C\uDF93" },
  cohousingKamer: { label: "Cohousing kamer", unit: "unit",     icon: "\uD83C\uDFE1" },
  parking:        { label: "Parking",         unit: "unit",     icon: "\uD83C\uDD7F\uFE0F" },
};

export const CATEGORY_LABELS: Record<ModuleCategory, string> = {
  algemeen: "Algemeen",
  vastgoed: "Vastgoed & beheer",
  documenten: "Documenten & communicatie",
  financieel: "Financieel & juridisch",
  acquisitie: "Acquisitie & due diligence",
  crm: "CRM & productiviteit",
  ai: "AI modules (tokenkost apart)",
  integraties: "Integraties",
};

export const CATEGORY_ORDER: ModuleCategory[] = [
  "algemeen", "vastgoed", "documenten", "financieel", "acquisitie", "crm", "ai", "integraties",
];

/** Helper to create a blank tier */
export function createBlankTier(label: string): TierConfig {
  return {
    label, icon: "\uD83D\uDCE6", description: "", color: "#6B7280",
    basePrice: 0, pricePerUnit: 0, pricePerM2: 0, includedUsers: 1, pricePerExtraUser: 0,
    includedModules: [],
  };
}

// ── Revenue helpers ──

export function customerTotalUnits(c: ManualCustomer): number {
  return Object.values(c.units).reduce((s, v) => s + v, 0);
}

function moduleReTypeCost(
  mod: ModuleItem,
  reKey: RealEstateKey,
  quantity: number,
  reModel: RealEstatePricingModelMap,
  catMult: CategoryMultipliers = DEFAULT_CATEGORY_MULTIPLIERS,
  volDisc: VolumeDiscounts = DEFAULT_VOLUME_DISCOUNTS
): number {
  if (!mod.enabled || quantity <= 0) return 0;
  const multiplier = catMult[reKey] ?? 1.0;
  const discount = getVolumeDiscount(quantity, volDisc[reKey] || []);
  const discountFactor = 1 - discount / 100;
  const basePrice = reModel[reKey] === "m2" ? mod.pricePerM2 : mod.pricePerUnit;
  return basePrice * multiplier * discountFactor * quantity;
}

/** Effective price for a specific RE category across all tier modules */
/** Effective per-unit/m² price for a category. Pass quantity to apply volume discount. */
export function effectiveCategoryPrice(
  reKey: RealEstateKey,
  tier: TierConfig,
  modulePricing: ModulePricing,
  reModel: RealEstatePricingModelMap = DEFAULT_RE_PRICING_MODEL,
  catMult: CategoryMultipliers = DEFAULT_CATEGORY_MULTIPLIERS,
  volDisc: VolumeDiscounts = DEFAULT_VOLUME_DISCOUNTS,
  quantity: number = 0
): number {
  const multiplier = catMult[reKey] ?? 1.0;
  const discount = quantity > 0 ? getVolumeDiscount(quantity, volDisc[reKey] || []) : 0;
  const discountFactor = 1 - discount / 100;
  return tier.includedModules.reduce((sum, modKey) => {
    const mod = modulePricing[modKey];
    if (!mod?.enabled) return sum;
    const basePrice = reModel[reKey] === "m2" ? mod.pricePerM2 : mod.pricePerUnit;
    return sum + basePrice * multiplier * discountFactor;
  }, 0);
}

export function customerModuleMRR(
  units: Record<RealEstateKey, number>,
  tier: TierConfig,
  modulePricing: ModulePricing,
  reModel: RealEstatePricingModelMap = DEFAULT_RE_PRICING_MODEL,
  catMult: CategoryMultipliers = DEFAULT_CATEGORY_MULTIPLIERS,
  volDisc: VolumeDiscounts = DEFAULT_VOLUME_DISCOUNTS
): number {
  let total = 0;
  for (const modKey of tier.includedModules) {
    const mod = modulePricing[modKey];
    if (!mod?.enabled) continue;
    for (const reKey of Object.keys(units) as RealEstateKey[]) {
      total += moduleReTypeCost(mod, reKey, units[reKey], reModel, catMult, volDisc);
    }
  }
  return total;
}

export function tierModuleCostPerUnit(tier: TierConfig, modulePricing: ModulePricing): number {
  return tier.includedModules.reduce((sum, modKey) => {
    const mod = modulePricing[modKey];
    return sum + (mod?.enabled ? mod.pricePerUnit : 0);
  }, 0);
}

export function tierModuleCostPerM2(tier: TierConfig, modulePricing: ModulePricing): number {
  return tier.includedModules.reduce((sum, modKey) => {
    const mod = modulePricing[modKey];
    return sum + (mod?.enabled ? mod.pricePerM2 : 0);
  }, 0);
}

/** Volume fee using tier defaults or customer overrides */
export function volumeFee(
  units: Record<RealEstateKey, number>,
  pricePerUnit: number,
  pricePerM2: number,
  reModel: RealEstatePricingModelMap = DEFAULT_RE_PRICING_MODEL
): number {
  let total = 0;
  for (const reKey of Object.keys(units) as RealEstateKey[]) {
    const qty = units[reKey];
    if (qty <= 0) continue;
    total += reModel[reKey] === "m2" ? pricePerM2 * qty : pricePerUnit * qty;
  }
  return total;
}

/** @deprecated alias */
export function tierVolumeFee(
  units: Record<RealEstateKey, number>,
  tier: TierConfig,
  reModel: RealEstatePricingModelMap = DEFAULT_RE_PRICING_MODEL
): number {
  return volumeFee(units, tier.pricePerUnit, tier.pricePerM2, reModel);
}

/**
 * Full recurring MRR for a manual customer.
 * Supports custom per-unit/m² overrides + extra user seats.
 */
/**
 * Full MRR for a manual customer.
 * Supports: custom module selection, volume discounts, additional commercial discount.
 * If fixedPricePerYear is set for the given year, returns that instead.
 */
export function customerMRRDetailed(
  customer: ManualCustomer,
  tier: TierConfig,
  modulePricing: ModulePricing,
  reModel: RealEstatePricingModelMap = DEFAULT_RE_PRICING_MODEL,
  catMult: CategoryMultipliers = DEFAULT_CATEGORY_MULTIPLIERS,
  volDisc: VolumeDiscounts = DEFAULT_VOLUME_DISCOUNTS,
  year?: number
): number {
  // Fixed negotiated price for a specific year overrides everything
  if (year && customer.fixedPricePerYear?.[year] != null) {
    return customer.fixedPricePerYear[year];
  }

  const extraUserCost = Math.max(0, (customer.extraUsers || 0) - tier.includedUsers) * tier.pricePerExtraUser;

  // Build effective tier config — if customer has custom modules, use those instead
  const effectiveTier: TierConfig = customer.selectedModules
    ? { ...tier, includedModules: customer.selectedModules }
    : tier;

  const moduleCost = customerModuleMRR(customer.units, effectiveTier, modulePricing, reModel, catMult, volDisc);
  let total = tier.basePrice + moduleCost + extraUserCost;

  // Apply additional commercial discount
  if (customer.additionalDiscountPct && customer.additionalDiscountPct > 0) {
    total = total * (1 - customer.additionalDiscountPct / 100);
  }

  return total;
}

/** MRR for a projected client (uses tier defaults with category multipliers + volume discounts) */
export function projectedClientMRR(
  avgUnits: Record<RealEstateKey, number>,
  tier: TierConfig,
  modulePricing: ModulePricing,
  reModel: RealEstatePricingModelMap = DEFAULT_RE_PRICING_MODEL,
  catMult: CategoryMultipliers = DEFAULT_CATEGORY_MULTIPLIERS,
  volDisc: VolumeDiscounts = DEFAULT_VOLUME_DISCOUNTS
): number {
  return tier.basePrice + customerModuleMRR(avgUnits, tier, modulePricing, reModel, catMult, volDisc);
}
